'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS } from '../config/api-config';
import axios from '../utils/axios-config';
import { checkMintStatus } from '../utils/minting/check-mint-status';
import { dkgOptions } from '../config/dkg-options';

export function useComments(oid, type = 'article') {
  const queryClient = useQueryClient();

  // Set query key and endpoints based on type
  const queryKey = ['comments', type, oid];
  const getCommentsEndpoint = API_ENDPOINTS.COMMENTS.GET_BY_OID(oid);
  let addCommentEndpoint;
  if (type === 'article') {
    addCommentEndpoint = API_ENDPOINTS.COMMENTS.ADD(oid);
  } else if (type === 'discussion') {
    addCommentEndpoint = API_ENDPOINTS.FORUM.DISCUSSIONS.COMMENT(oid);
  } else {
    throw new Error(
      `Invalid comment type: ${type}. Must be 'article' or 'discussion'.`
    );
  }

  // Get comments by OID (article or discussion)
  const {
    data: comments,
    isLoading: isCommentsLoading,
    error: commentsError,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!oid) return [];
      const response = await axios.get(getCommentsEndpoint);
      return response.data.comments || [];
    },
    enabled: !!oid,
  });

  // Add a comment (article or discussion)
  const addCommentMutation = useMutation({
    mutationFn: async ({ content, creator, commentType }) => {
      const response = await axios.post(addCommentEndpoint, {
        content,
        creator,
        commentType:
          commentType ||
          (type === 'article' ? 'articleComment' : 'discussionComment'),
        options: dkgOptions,
      });

      // Check if the upload was successful and a job ID was returned
      if (response.data && response.data.success && response.data.jobID) {
        // Step 2: Poll the mint status until completion or failure
        const mintStatus = await checkMintStatus(
          response.data.jobID,
          4000, // Check every 4 seconds
          90 // Maximum 90 attempts (6 minutes)
        );
        if (!mintStatus || !mintStatus.success) {
          throw new Error(
            mintStatus?.error || 'Failed to mint comment NFT. Please try again.'
          );
        }
        // Return combined result if successful
        return {
          ...response.data,
          mintStatus: mintStatus.state,
          mintSuccess: mintStatus.success,
          error: mintStatus.error || null,
        };
      } else {
        throw new Error('Failed to add comment. No job ID returned.');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Add a reply to a comment
  const addReplyMutation = useMutation({
    mutationFn: async ({ commentOid, content, creator }) => {
      const response = await axios.post(
        API_ENDPOINTS.COMMENTS.ADD_REPLY(commentOid),
        {
          content,
          creator,
        }
      );
      return response.data.reply;
    },
    onSuccess: (_, variables) => {
      // Invalidate both the parent comment and the article's comments
      queryClient.invalidateQueries({
        queryKey: ['comments', 'article', oid],
      });
      queryClient.invalidateQueries({
        queryKey: ['replies', variables.commentOid],
      });
    },
  });

  // Get replies for a comment
  const useGetReplies = (commentOid) => {
    return useQuery({
      queryKey: ['replies', commentOid],
      queryFn: async () => {
        const response = await axios.get(
          API_ENDPOINTS.COMMENTS.GET_REPLIES(commentOid)
        );
        return response.data.replies || [];
      },
      enabled: !!commentOid,
    });
  };

  // Like interaction (optimistic update)
  const likeInteraction = useMutation({
    mutationFn: async ({ userAddress, commentOid, uOID }) => {
      if (!userAddress || !commentOid || !uOID) {
        throw new Error('userAddress, commentOid, and uOID are required');
      }
      const response = await axios.put(
        API_ENDPOINTS.COMMENTS.LIKE(commentOid),
        {
          userAddress,
          sOID: commentOid,
          uOID,
          interactionType: 'like',
        }
      );
      return response.data;
    },
    onMutate: async ({ commentOid }) => {
      await queryClient.cancelQueries();

      // Optimistically update the specific comment in the comments list
      const previousComments = queryClient.getQueryData(queryKey);
      if (Array.isArray(previousComments)) {
        queryClient.setQueryData(
          queryKey,
          previousComments.map((comment) =>
            comment.oid === commentOid
              ? { ...comment, likes: (comment.likes || 0) + 1 }
              : comment
          )
        );
      }

      // Optimistically update replies if this is a reply
      const replyQueries = queryClient.getQueriesData({
        queryKey: ['replies'],
      });
      const previousRepliesByKey = {};
      replyQueries.forEach(([key, previousReplies]) => {
        if (Array.isArray(previousReplies)) {
          previousRepliesByKey[JSON.stringify(key)] = previousReplies;
          queryClient.setQueryData(
            key,
            previousReplies.map((reply) =>
              reply.oid === commentOid
                ? { ...reply, likes: (reply.likes || 0) + 1 }
                : reply
            )
          );
        }
      });

      return { previousComments, previousRepliesByKey };
    },
    onError: (err, variables, context) => {
      if (context?.previousComments) {
        queryClient.setQueryData(queryKey, context.previousComments);
      }
      if (context?.previousRepliesByKey) {
        Object.entries(context.previousRepliesByKey).forEach(([key, data]) => {
          queryClient.setQueryData(JSON.parse(key), data);
        });
      }
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: ['replies', variables.commentOid],
      });
    },
  });

  // Dislike interaction (optimistic update)
  const dislikeInteraction = useMutation({
    mutationFn: async ({ userAddress, commentOid, uOID }) => {
      if (!userAddress || !commentOid || !uOID) {
        throw new Error('userAddress, commentOid, and uOID are required');
      }
      const response = await axios.put(
        API_ENDPOINTS.COMMENTS.DISLIKE(commentOid),
        {
          userAddress,
          sOID: commentOid,
          uOID,
          interactionType: 'dislike',
        }
      );
      return response.data;
    },
    onMutate: async ({ commentOid }) => {
      await queryClient.cancelQueries();

      // Optimistically update the specific comment in the comments list
      const previousComments = queryClient.getQueryData(queryKey);
      if (Array.isArray(previousComments)) {
        queryClient.setQueryData(
          queryKey,
          previousComments.map((comment) =>
            comment.oid === commentOid
              ? { ...comment, dislikes: (comment.dislikes || 0) + 1 }
              : comment
          )
        );
      }

      // Optimistically update replies if this is a reply
      const replyQueries = queryClient.getQueriesData({
        queryKey: ['replies'],
      });
      const previousRepliesByKey = {};
      replyQueries.forEach(([key, previousReplies]) => {
        if (Array.isArray(previousReplies)) {
          previousRepliesByKey[JSON.stringify(key)] = previousReplies;
          queryClient.setQueryData(
            key,
            previousReplies.map((reply) =>
              reply.oid === commentOid
                ? { ...reply, dislikes: (reply.dislikes || 0) + 1 }
                : reply
            )
          );
        }
      });

      return { previousComments, previousRepliesByKey };
    },
    onError: (err, variables, context) => {
      if (context?.previousComments) {
        queryClient.setQueryData(queryKey, context.previousComments);
      }
      if (context?.previousRepliesByKey) {
        Object.entries(context.previousRepliesByKey).forEach(([key, data]) => {
          queryClient.setQueryData(JSON.parse(key), data);
        });
      }
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: ['replies', variables.commentOid],
      });
    },
  });

  // Update a comment
  const updateCommentMutation = useMutation({
    mutationFn: async ({ commentOid, content, creator }) => {
      const response = await axios.put(
        API_ENDPOINTS.COMMENTS.UPDATE(commentOid),
        {
          content,
          creator,
        }
      );
      return response.data.comment;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['comments', 'article', oid],
      });
      queryClient.invalidateQueries({
        queryKey: ['replies', variables.commentOid],
      });
    },
  });

  // Delete a comment
  const deleteCommentMutation = useMutation({
    mutationFn: async ({ commentOid, creator, isAdmin }) => {
      const response = await axios.delete(
        API_ENDPOINTS.COMMENTS.DELETE(commentOid),
        {
          data: { creator, isAdmin },
        }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['comments', 'article', oid],
      });
      queryClient.invalidateQueries({
        queryKey: ['replies', variables.commentOid],
      });
    },
  });

  // Get a comment by ID
  const getCommentByIdQuery = (commentOid) => {
    return useQuery({
      queryKey: ['comment', commentOid],
      queryFn: async () => {
        const response = await axios.get(
          API_ENDPOINTS.COMMENTS.GET_BY_ID(commentOid)
        );
        return response.data.comment;
      },
      enabled: !!commentOid,
    });
  };

  return {
    comments,
    isCommentsLoading,
    commentsError,
    addComment: addCommentMutation,
    addDiscussionComment: addCommentMutation, // alias for compatibility
    addReply: addReplyMutation,
    getReplies: useGetReplies,
    toggleLike: likeInteraction, // Updated to use optimistic update
    likeInteraction: () => likeInteraction,
    dislikeInteraction: () => dislikeInteraction,
    updateComment: updateCommentMutation,
    deleteComment: deleteCommentMutation,
    getCommentById: (commentOid) => getCommentByIdQuery(commentOid),
  };
}

// Get comments by user (hook)
export function useGetCommentsByUser(userId) {
  return useQuery({
    queryKey: ['comments', 'user', userId],
    queryFn: async () => {
      if (!userId) return [];
      const response = await axios.get(
        API_ENDPOINTS.COMMENTS.GET_BY_USER(userId)
      );
      // API returns { comments: [...] }
      return response.data.comments || [];
    },
    enabled: !!userId,
  });
}

// Get all comments by a user (not a hook)
export async function getCommentsByUser(userId) {
  if (!userId) return [];
  const response = await axios.get(API_ENDPOINTS.COMMENTS.GET_BY_USER(userId));
  return response.data.comments || [];
}
